import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HomeOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigationTree } from './useNavigationTree';
import { flattenNavItems } from './navigationConfig';
import './shell-breadcrumb.css';

/**
 * Auto-derived "Home / Group / Page" trail for every route, built from the
 * same nav tree the sidebar renders — no per-page opt-in required. That tree is
 * the backend access-module catalog when it is live, so a label or group an
 * operator edits reads the same here as in the sidebar.
 * Falls back to rendering nothing on routes with no nav representation
 * (e.g. deep action/detail pages a few levels below the nav tree).
 */
const findMatch = (tree, pathname) => {
  // Children are nav items too: a page under a parent (Encounters → Facility summary)
  // reads "Group / Parent / Page".
  const flat = tree
    .flatMap((group) => group.items.flatMap((item) => [
      { group: group.group, item, parent: null },
      ...flattenNavItems(item.children || []).map((child) => ({ group: group.group, item: child, parent: item })),
    ]))
    .filter((entry) => entry.item.path)
    .map((entry) => ({ ...entry, pathname: String(entry.item.path).split('?')[0] }))
    .sort((a, b) => b.pathname.length - a.pathname.length);

  return flat.find(
    ({ pathname: itemPath }) => pathname === itemPath || pathname.startsWith(`${itemPath}/`),
  ) || null;
};

const ShellBreadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationTree = useNavigationTree();

  const match = useMemo(
    () => findMatch(navigationTree, location.pathname),
    [navigationTree, location.pathname],
  );

  if (!match || match.item.path === '/dashboard') return null;

  return (
    <nav className="shell-breadcrumb" aria-label="Breadcrumb">
      <button
        type="button"
        className="shell-breadcrumb-link"
        onClick={() => navigate('/dashboard')}
      >
        <HomeOutlined />
      </button>
      <RightOutlined className="shell-breadcrumb-sep" aria-hidden />
      <span className="shell-breadcrumb-group">{match.group}</span>
      <RightOutlined className="shell-breadcrumb-sep" aria-hidden />
      {match.parent ? (
        <>
          <button
            type="button"
            className="shell-breadcrumb-link"
            onClick={() => navigate(match.parent.path)}
          >
            {match.parent.label}
          </button>
          <RightOutlined className="shell-breadcrumb-sep" aria-hidden />
        </>
      ) : null}
      <span className="shell-breadcrumb-current" aria-current="page">
        {match.item.label}
      </span>
    </nav>
  );
};

export default ShellBreadcrumb;
